import { useStore } from '@nanostores/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { listWorkspaceObjectTypes } from './api';
import { DeliverySettings } from './types';
import type { MergedWorkspace } from './types';
import { useSampleWorkspace } from './sample-workspace';
import { authStore } from '@/services/auth/store';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import type { Workspace } from '@/services/auth/types';
import { fetchAccountById } from '@/services/auth/account-api';

const DEFAULT_PARAMS = {
  enabled: true,
  includeSampleWorkspace: false,
};

interface UseListWorkspacesParamsWithSelect<T> {
  enabled?: boolean;
  select: (data: MergedWorkspace[]) => T;
}

interface UseListWorkspacesParamsWithoutSelect {
  enabled?: boolean;
}

export function useListWorkspaces<T>(
  params: UseListWorkspacesParamsWithSelect<T>,
): ReturnType<typeof useQuery<MergedWorkspace[], unknown, T, readonly unknown[]>>;
export function useListWorkspaces(
  params?: UseListWorkspacesParamsWithoutSelect,
): ReturnType<typeof useQuery<MergedWorkspace[], unknown, MergedWorkspace[], readonly unknown[]>>;
export function useListWorkspaces<T = MergedWorkspace[]>(
  useListWorkspacesParams?: UseListWorkspacesParamsWithSelect<T> | UseListWorkspacesParamsWithoutSelect,
) {
  const params = {
    ...DEFAULT_PARAMS,
    ...useListWorkspacesParams,
  };

  const queryClient = useQueryClient();

  const auth = useStore(authStore);
  const accountId = auth.account?.id;

  const { sampleAccountId, sampleWorkspaceId } = useSampleWorkspace();

  const selectFunction = 'select' in params ? (params as UseListWorkspacesParamsWithSelect<T>).select : undefined;

  return useQuery<MergedWorkspace[], unknown, T, readonly unknown[]>({
    queryKey: ['workspaces', 'list', sampleAccountId, sampleWorkspaceId],
    queryFn: async () => {
      if (accountId == null) {
        return Promise.reject();
      }

      const { environments: rawWorkspaces } = await fetchAccountById(accountId);

      // If the user has no workspaces available (e.g. users that only handle billing)
      // we should return an empty array instead of throwing an error
      if (rawWorkspaces == null) {
        return [];
      }

      const workspaces: MergedWorkspace[] = rawWorkspaces.map(getMergedWorkspaceFromRaw);

      if (sampleAccountId != null && sampleWorkspaceId != null) {
        const sampleWorkspace: MergedWorkspace = {
          id: sampleWorkspaceId,
          account_id: sampleAccountId,
          cio_workspace_id: sampleWorkspaceId,
          cdp_workspace_id: sampleWorkspaceId,
          cio_initialized: true,
          cdp_initialized: true,
          name: 'Sample Workspace',
          color: '#000000',
          delivery_settings: DeliverySettings.Normal,
          delivery_email: '',
        };
        workspaces.push(sampleWorkspace);
      }

      for (const w of workspaces) {
        queryClient.setQueryData(
          ['workspaces', { cdp_workspace_id: w.cdp_workspace_id, cio_workspace_id: w.cdp_workspace_id }],
          w,
        );
      }
      return workspaces;
    },
    ...(selectFunction != null ? { select: selectFunction } : {}),
    staleTime: 60 * 1000,
    enabled: accountId != null && params.enabled,
  });
}

export const useWorkspaceCount = () => {
  return useListWorkspaces({
    select: (data) => data.length,
  });
};

interface UseFilterWorkspacesByNameOptions {
  includeSampleWorkspace?: boolean;
}
export const useFilterWorkspacesByName = (
  name: string,
  { includeSampleWorkspace = false }: UseFilterWorkspacesByNameOptions = {},
) => {
  const lowerCaseName = name.toLowerCase();
  const { isSampleWorkspace } = useSampleWorkspace();

  return useListWorkspaces({
    select: (data) => {
      const sampleWorkspace = data.find((w) => isSampleWorkspace(w.cio_workspace_id));

      const shouldIncludeSampleWorkspace = includeSampleWorkspace && sampleWorkspace != null;
      return (
        data
          .filter((w) => w.name.toLowerCase().includes(lowerCaseName) && !isSampleWorkspace(w.cio_workspace_id))
          .sort((a, b) => a.name.localeCompare(b.name))
          // Add demo workspace to the end of the list
          .concat(shouldIncludeSampleWorkspace ? [sampleWorkspace] : [])
      );
    },
  });
};

export const useListWorkspaceObjectTypes = (workspaceId?: number) => {
  const auth = useStore(authStore);
  const accountId = auth.account?.id;
  const builder = isBuilderPlan(auth.account);

  return useQuery({
    queryKey: ['workspaces', workspaceId, 'object_types'],
    queryFn: async () => {
      if (accountId == null || workspaceId == null) {
        return Promise.reject();
      }

      return listWorkspaceObjectTypes(accountId, workspaceId);
    },
    staleTime: 60 * 1000,
    enabled: accountId != null && workspaceId != null && !builder,
  });
};

// TODO: would be great if someone cleaned this up someday
function getMergedWorkspaceFromRaw(workspace: Workspace): MergedWorkspace {
  const workspaceId = +workspace.id;
  return {
    id: workspaceId,
    account_id: +workspace.account_id,
    cio_workspace_id: workspaceId,
    cdp_workspace_id: workspaceId,
    cio_initialized: true,
    cdp_initialized: true,
    name: workspace.name,
    color: workspace.hex_code ?? '',
    delivery_settings: (workspace.delivery_settings as DeliverySettings) ?? DeliverySettings.Normal,
    delivery_email: workspace.delivery_email,
  };
}
